/* eslint-disable @trello/filename-case */
/* eslint-disable @trello/export-matches-filename */

import { euCountries, usTerritories } from './countries';

/**
 * Checks for postal codes that begin with G, H or J in Canada
 * @see https://en.wikipedia.org/wiki/Postal_codes_in_Canada
 */
export const isQuebec = (country: string, zipCode?: string): boolean => {
  return country === 'CA' && /^[GHJ]/i.test(zipCode || '');
};

/**
 * Check if the country requires a Tax ID number
 * - EU countries, Norway and The Republic of Korea require a VAT number
 * - Australia requires an ABN number
 * - New Zealand requires a GST number
 * - Quebec, Canada requires a QST number
 *
 * Should mimic similar validation logic in shop. See
 * @see https://bitbucket.org/trello/aardvark/src/master/Aardvark/Accounting/CAvalara.cs
 */
export const requiresTaxId = (country: string, zipCode?: string): boolean => {
  return (
    euCountries.indexOf(country) !== -1 ||
    isQuebec(country, zipCode) ||
    ['AU', 'KR', 'NO', 'NZ'].indexOf(country) !== -1
  );
};

// Keeping this alias for backwards-compat with old coffee module until refactor is complete
export const hasTaxNumbers = requiresTaxId;

/**
 * Returns the appropriate abbreviation for the type of Tax ID required by
 * the given country / zipCode combination
 */
export const getTaxType = (
  country: string,
  zipCode?: string,
): 'ABN' | 'GST' | 'QST' | 'VAT' | '' => {
  if (!requiresTaxId(country, zipCode)) {
    return '';
  }
  if (country === 'AU') {
    return 'ABN';
  } else if (country === 'NZ') {
    return 'GST';
  } else if (isQuebec(country, zipCode)) {
    return 'QST';
  } else {
    return 'VAT';
  }
};

/**
 * For a country / zip code combination that requires a tax ID number,
 * checks that it is within the valid length range. Does not attempt to
 * validate the taxable entity or do any further pattern matching than
 * just checking the length. Proper validation is done server-side.
 *
 * @see https://bitbucket.org/trello/aardvark/src/master/Aardvark/Accounting/CAvalara.cs
 * @see https://en.wikipedia.org/wiki/VAT_identification_number
 */
export const isValidTaxId = (
  taxId: string = '',
  country: string = '',
  zipCode?: string,
): boolean => {
  if (!requiresTaxId(country, zipCode)) {
    return true;
  }
  if (taxId && taxId.length) {
    const taxDigitsLength = taxId.replace(/\D/g, '').length;
    const maxLength = isQuebec(country, zipCode) ? 14 : 12;
    return taxDigitsLength >= 2 && taxDigitsLength <= maxLength;
  } else {
    return true;
  }
};

/**
 * Checks if a given country requires a zip code to be entered. These include
 * - United States
 * - US Territories
 * - Canada
 */
export const requiresZipCode = (country: string): boolean => {
  return (
    country === 'US' || country === 'CA' || usTerritories.includes(country)
  );
};

/**
 * For a country that requires a zip code, validates that the given
 * zip code looks valid
 * - USA format: 5 or 9 digits
 * - Canadian format: A1A-1A1
 */
export const isValidZipCode = (zipCode: string, country: string): boolean => {
  if (country === 'US' || usTerritories.includes(country)) {
    const ref = zipCode.replace(/\D/g, '').length;
    return ref === 5 || ref === 9;
  } else if (country === 'CA') {
    return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(zipCode);
  } else {
    return false;
  }
};
